import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import type { SystemicIssueAnalysis } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, StatCard, StatusBadge } from '../../ui/components';

/** GET /systemic-issues/:id — publish pushes the recommendations to C11 OpenGov as a GAZETTE notice. */
export function SystemicIssueDetail() {
  const { id } = useParams<{ id: string }>();
  const analysisQ = useApiQuery<SystemicIssueAnalysis>(id ? `/api/v1/m40/systemic-issues/${id}` : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (analysisQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading analysis…</div>;
  if (analysisQ.error) return <ErrorNote message={analysisQ.error} />;
  const a = analysisQ.data;
  if (!a) return null;

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m40/systemic-issues/${a.id}/publish`);
      analysisQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Publish failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title={a.refNo} subtitle={`${a.subjectMda}${a.category ? ` · ${a.category.replaceAll('_', ' ')}` : ''}`} actions={<StatusBadge status={a.status} />} />

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Period" value={`${formatDate(a.periodStart)} – ${formatDate(a.periodEnd)}`} />
        <StatCard label="Complaint count" value={a.complaintCount} />
        <StatCard label="Sample refs" value={a.sampleComplaintRefs.length} />
        <StatCard label="Published" value={a.publishedAt ? formatDateTime(a.publishedAt) : 'Not yet'} />
      </div>

      <div className="card mb-4 p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Findings</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{a.findings}</p>
      </div>
      <div className="card mb-6 p-5">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Recommendations</h2>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{a.recommendations}</p>
      </div>

      {a.sampleComplaintRefs.length > 0 ? (
        <div className="mb-6 text-xs text-slate-500">Sample complaint references: {a.sampleComplaintRefs.join(', ')}</div>
      ) : null}

      {a.status === 'DRAFT' ? (
        <button className="btn-primary" onClick={publish} disabled={busy}>
          {busy ? 'Publishing…' : 'Publish to C11 OpenGov (Gazette)'}
        </button>
      ) : (
        <p className="text-xs text-emerald-700">Published — notice {a.publishedNoticeId}.</p>
      )}
    </div>
  );
}
